import React, { useEffect, useState } from 'react';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  ThemeProvider,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useTranslation } from 'react-i18next';
import AuthLayout from './components/AuthLayout';
import { theme, colors } from './theme';

type VerifyState = 'checking' | 'success' | 'expired' | 'error';

export default function Verify(_props: { theme?: any }) {
  const { t } = useTranslation('auth');
  const [state, setState] = useState<VerifyState>('checking');
  const [message, setMessage] = useState(t('verify.checking'));
  const [email, setEmail] = useState('');

  useEffect(() => {
    const params = Object.fromEntries(
      new URLSearchParams(window.location.search).entries()
    );

    if (!params.token) {
      setState('error');
      setMessage(t('verify.missingToken'));
      return;
    }

    try {
      const decoded: JwtPayload & { email: string } = jwtDecode(params.token);
      if (decoded.exp && new Date(decoded.exp * 1000) < new Date()) {
        setEmail(decoded.email);
        setState('expired');
        setMessage(t('verify.expired', { email: decoded.email }));
        return;
      }
    } catch {
      setState('error');
      setMessage(t('verify.invalid'));
      return;
    }

    fetch(`${process.env.REACT_APP_API}/users/verify/email`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${params.token}`,
      },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.verifiedEmail) {
          setState('success');
          setMessage(t('verify.success'));
        } else {
          setState('error');
          setMessage(data.message || t('verify.failed'));
        }
      })
      .catch((err) => {
        setState('error');
        setMessage(err.message || t('verify.failed'));
      });
  }, [t]);

  const resend = () => {
    fetch(`${process.env.REACT_APP_API}/users/verify/email/${email}`)
      .then((r) => r.json())
      .then((data) => {
        setState('checking');
        setMessage(data.message || t('verify.resent', { email }));
      })
      .catch((err) => setMessage(err.message));
  };

  return (
    <ThemeProvider theme={theme}>
      <AuthLayout title={t('verify.title')}>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          {state === 'checking' && (
            <CircularProgress sx={{ color: colors.brand, mb: 3 }} />
          )}
          {state === 'success' && (
            <CheckCircleIcon sx={{ fontSize: 64, color: colors.brand, mb: 2 }} />
          )}
          {(state === 'error' || state === 'expired') && (
            <ErrorOutlineIcon sx={{ fontSize: 64, color: colors.warning, mb: 2 }} />
          )}

          <Typography
            variant="body1"
            sx={{ color: colors.textMuted, mb: 3, lineHeight: 1.6 }}
          >
            {message}
          </Typography>

          {state === 'success' && (
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => (window.location.href = '/login')}
              sx={{ py: 1.5 }}
            >
              {t('verify.signIn')}
            </Button>
          )}

          {state === 'expired' && (
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={resend}
              sx={{ py: 1.5 }}
            >
              {t('verify.sendNewLink')}
            </Button>
          )}

          {state === 'error' && (
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={() => (window.location.href = '/login')}
              sx={{ py: 1.5 }}
            >
              {t('verify.backToSignIn')}
            </Button>
          )}
        </Box>
      </AuthLayout>
    </ThemeProvider>
  );
}
